import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import GuestOverlay from "@/components/auth/GuestOverlay";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import {
  Image,
  Sparkles,
  Loader2,
  Download,
  Coins,
  AlertTriangle,
  History,
  Plus,
  Wand2,
  Youtube,
  Facebook,
  Monitor,
  Square,
  Repeat,
  Trash2,
  RefreshCw,
  CheckCircle2,
  FastForward
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CoverGenerator = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, loading: authLoading, profile } = useAuth();
  const { toast } = useToast();
  const { generate, isGenerating, userCredits } = useGeneration();

  const photoModels = [
    { id: "gpt-image", name: "GPT Image", credits: 4 },
    { id: "seedream", name: "Seedream 4.5", credits: 6.5 },
    { id: "nanobana", name: "Nano Banana", credits: 4 },
    { id: "nanobana-pro", name: "Nano Banana Pro", credits: 18, hasResolution: true },
  ];

  // Cover type presets
  const coverTypes = [
    {
      id: "youtube",
      name: "YouTube",
      icon: Youtube,
      ratio: "16:9",
      descTh: "ขนาด 2560x1440 สำหรับแบนเนอร์ช่อง",
      descEn: "2560x1440 for channel banner"
    },
    {
      id: "facebook",
      name: "Facebook",
      icon: Facebook,
      ratio: "16:9",
      descTh: "ขนาด 820x312 สำหรับปกเพจ",
      descEn: "820x312 for page cover"
    },
    {
      id: "youtube-thumbnail",
      name: "YouTube Thumbnail",
      icon: Monitor,
      ratio: "16:9",
      descTh: "ขนาด 1280x720 สำหรับ Thumbnail วิดีโอ",
      descEn: "1280x720 for video thumbnail"
    },
  ];

  // Prompt templates for cover images
  const promptTemplates = [
    {
      id: "gaming",
      labelTh: "เกม",
      labelEn: "Gaming",
      prompt: "Professional gaming channel banner, neon lights, futuristic design, dark background with colorful accents, high quality, 4K"
    },
    {
      id: "tech",
      labelTh: "เทคโนโลยี",
      labelEn: "Technology",
      prompt: "Modern tech channel banner, minimalist design, blue and white color scheme, circuit patterns, clean and professional, 4K"
    },
    {
      id: "travel",
      labelTh: "ท่องเที่ยว",
      labelEn: "Travel",
      prompt: "Travel vlog channel banner, beautiful landscape collage, adventure theme, warm sunset colors, wanderlust vibes, 4K"
    },
    {
      id: "food",
      labelTh: "อาหาร",
      labelEn: "Food",
      prompt: "Food channel banner, delicious gourmet dishes, warm colors, rustic wooden background, professional food photography style, 4K"
    },
    {
      id: "music",
      labelTh: "ดนตรี",
      labelEn: "Music",
      prompt: "Music channel banner, abstract sound waves, colorful gradient, modern design, dynamic and energetic, 4K"
    },
    {
      id: "business",
      labelTh: "ธุรกิจ",
      labelEn: "Business",
      prompt: "Professional business channel banner, corporate style, blue and gold accents, modern office aesthetic, clean typography space, 4K"
    },
  ];

  // Tab state: "cover" or "icon"
  const [activeTab, setActiveTab] = useState<"cover" | "icon">("cover");

  const [promptText, setPromptText] = useState("");
  const [selectedPhotoModel, setSelectedPhotoModel] = useState("nanobana");
  const [selectedCoverType, setSelectedCoverType] = useState("youtube");
  const [selectedResolution, setSelectedResolution] = useState<"1K" | "2K" | "4K">("1K");
  const [generatedResult, setGeneratedResult] = useState<string | null>(null);
  const [lastGeneration, setLastGeneration] = useState<{ model: string; credits: number } | null>(null);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [allowNewGeneration, setAllowNewGeneration] = useState(false);

  // Icon prompt templates - designed specifically for 1:1 icon/logo generation
  const iconPromptTemplates = [
    {
      id: "logo-modern",
      labelTh: "โลโก้โมเดิร์น",
      labelEn: "Modern Logo",
      prompt: "Minimalist modern logo icon, simple geometric shapes, flat design, centered composition, solid color background, professional branding style, square format 1:1, clean vector art style"
    },
    {
      id: "avatar-cartoon",
      labelTh: "อวาตาร์การ์ตูน",
      labelEn: "Cartoon Avatar",
      prompt: "Cute cartoon character avatar icon, simple kawaii style, round face, pastel colors, centered portrait, friendly expression, square format 1:1, profile picture style"
    },
    {
      id: "gaming-mascot",
      labelTh: "มาสคอตเกม",
      labelEn: "Gaming Mascot",
      prompt: "Gaming mascot icon, bold colors, dynamic pose, esports style, centered composition, dark background with glow effects, square format 1:1, professional gaming logo"
    },
    {
      id: "tech-icon",
      labelTh: "ไอคอนเทคโนโลยี",
      labelEn: "Tech Icon",
      prompt: "Technology app icon, gradient colors, sleek modern design, 3D depth effect, rounded corners style, professional tech branding, square format 1:1, app icon design"
    },
    {
      id: "nature-icon",
      labelTh: "ธรรมชาติ",
      labelEn: "Nature",
      prompt: "Nature-themed icon, leaf or tree symbol, eco-friendly green colors, simple organic shapes, centered design, clean background, square format 1:1, environmental branding"
    },
    {
      id: "food-icon",
      labelTh: "อาหาร",
      labelEn: "Food",
      prompt: "Food brand icon, appetizing design, warm colors, simple food illustration, centered composition, clean background, square format 1:1, restaurant or food delivery logo style"
    },
  ];

  // Recent covers state
  interface CoverLog {
    id: string;
    prompt: string;
    display_prompt?: string | null;
    result_url: string | null;
    status: string;
    created_at: string;
    model: string;
    credits_used?: number;
  }

  const [recentCovers, setRecentCovers] = useState<CoverLog[]>([]);
  const [loadingCovers, setLoadingCovers] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);

  // Fetch recent covers (photo with sub_category = 'cover' or 'icon')
  const fetchRecentCovers = async () => {
    if (!user) return;
    setLoadingCovers(true);
    try {
      // Query photos that have sub_category = cover or icon
      // Fallback: if sub_category column doesn't exist yet, just fetch recent photos
      const { data, error } = await supabase
        .from("generation_logs")
        .select("id, prompt, display_prompt, result_url, status, created_at, model, credits_used")
        .eq("user_id", user.id)
        .eq("service_type", "photo")
        .order("created_at", { ascending: false })
        .limit(8);

      if (error) throw error;
      setRecentCovers((data || []) as CoverLog[]);
    } catch (err) {
      console.error("Error fetching recent covers:", err);
      setRecentCovers([]);
    } finally {
      setLoadingCovers(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchRecentCovers();
    }
  }, [user, generatedResult]);

  const getRequiredCredits = () => {
    const model = photoModels.find(m => m.id === selectedPhotoModel);
    if (!model) return 4;

    // Nano Banana Pro has resolution-based pricing
    if (selectedPhotoModel === "nanobana-pro") {
      return selectedResolution === "4K" ? 24 : 16; // 4K = 24 credits, 1K/2K = 16 credits
    }

    return model.credits;
  };

  const getSelectedModelName = () => {
    return photoModels.find(m => m.id === selectedPhotoModel)?.name || "";
  };

  const hasEnoughCredits = authLoading || userCredits === undefined ? true : userCredits >= getRequiredCredits();

  const handleGenerate = async () => {
    if (!promptText.trim()) return;
    if (isGenerating && !allowNewGeneration) return;

    const requiredCredits = getRequiredCredits();
    const modelName = getSelectedModelName();

    // Reset states for new generation
    setAllowNewGeneration(false);
    setShowContinueButton(true);

    // Determine ratio based on active tab
    let ratio = "16:9";
    if (activeTab === "icon") {
      ratio = "1:1";
    } else {
      const coverType = coverTypes.find(c => c.id === selectedCoverType);
      ratio = coverType?.ratio || "16:9";
    }

    // Build options object
    const options: Record<string, any> = { ratio };

    // Add resolution for Nano Banana Pro model
    if (selectedPhotoModel === "nanobana-pro") {
      options.resolution = selectedResolution;
      options.nanobanaPro = true;
    }

    const result = await generate(
      "photo",
      modelName,
      promptText,
      requiredCredits,
      options
    );

    // Hide continue button when generation completes
    setShowContinueButton(false);

    if (result.success && result.resultUrl) {
      setGeneratedResult(result.resultUrl);
      setLastGeneration({ model: modelName, credits: requiredCredits });

      // Update sub_category to "cover" or "icon" for history filtering
      if (result.logId) {
        const subCategory = activeTab === "icon" ? "icon" : "cover";
        await supabase
          .from("generation_logs")
          .update({ sub_category: subCategory } as any)
          .eq("id", result.logId);
      }
    }

    fetchRecentCovers();
  };

  const handleContinueWithoutWaiting = () => {
    setShowContinueButton(false);
    setAllowNewGeneration(true);
    toast({
      title: t("สร้างต่อได้เลย!", "You can continue!"),
      description: t("ผลลัพธ์ก่อนหน้าจะเก็บไว้ที่หน้า History", "Previous result will be saved to History"),
    });
  };

  const handleTemplateClick = (template: typeof promptTemplates[0]) => {
    setPromptText(template.prompt);
  };

  const handleReusePrompt = (prompt: string) => {
    setPromptText(prompt);
    window.scrollTo(0, 0);
  };

  const formatDateHistory = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDeleteHistory = async (id: string) => {
    try {
      if (!confirm(t("คุณแน่ใจหรือไม่ที่จะลบรายการนี้?", "Are you sure you want to delete this item?"))) {
        return;
      }

      const { error } = await supabase
        .from("generation_logs")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: t("ลบสำเร็จ", "Deleted successfully"),
        description: t("รายการถูกลบออกจากประวัติแล้ว", "The item has been removed from history"),
      });

      fetchRecentCovers();
    } catch (error: any) {
      toast({
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">{t("สำเร็จ", "Completed")}</Badge>;
      case "processing":
        return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 animate-pulse">{t("กำลังประมวลผล", "Processing")}</Badge>;
      case "pending":
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">{t("รอดำเนินการ", "Pending")}</Badge>;
      case "failed":
        return <Badge variant="destructive">{t("ล้มเหลว", "Failed")}</Badge>;
      default:
        return null;
    }
  };

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Empty file');
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
                  <Image className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-heading font-bold">
                    {t("สร้างภาพปก", "Cover Generator")}
                  </h1>
                  <p className="text-muted-foreground">
                    {t("สร้างภาพปก YouTube / Facebook ด้วย AI", "Generate YouTube / Facebook covers with AI")}
                  </p>
                </div>
              </div>
            </div>
            {/* Credit Balance Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/10 border border-primary/20 cursor-pointer hover:bg-primary/20 transition-all group">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                    <Coins className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("เครดิตคงเหลือ", "Remaining Credits")}</p>
                    <p className="text-xl font-bold text-primary">{(userCredits || 0).toLocaleString()}</p>
                  </div>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/history")} className="gap-2 cursor-pointer">
                  <History className="h-4 w-4" />
                  <span>{t("แสดงประวัติ", "Show History")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pricing")} className="gap-2 cursor-pointer">
                  <Plus className="h-4 w-4 text-primary" />
                  <span className="text-primary font-medium">{t("เติมเครดิต", "Top Up")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Guest Login Prompt */}
          {!user && (
            <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-xl flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-primary">
                <Sparkles className="h-5 w-5" />
                <span className="text-sm font-medium">{t("เข้าสู่ระบบเพื่อเริ่มสร้างสรรค์!", "Login to start creating!")}</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate("/login")}>{t("เข้าสู่ระบบ", "Login")}</Button>
                <Button variant="hero" size="sm" onClick={() => navigate("/register")}>{t("สมัครสมาชิก", "Register")}</Button>
              </div>
            </div>
          )}

          {/* Tab Switch */}
          <div className="mb-6">
            <div className="flex gap-2 p-1 bg-muted/50 rounded-xl w-fit">
              <button
                onClick={() => {
                  setActiveTab("cover");
                  setPromptText("");
                  setGeneratedResult(null);
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${activeTab === "cover"
                  ? "bg-background shadow-sm text-primary"
                  : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                <Image className="h-4 w-4" />
                {t("ภาพปก", "Cover")}
              </button>
              <button
                onClick={() => {
                  setActiveTab("icon");
                  setPromptText("");
                  setGeneratedResult(null);
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${activeTab === "icon"
                  ? "bg-background shadow-sm text-green-600"
                  : "text-muted-foreground hover:text-foreground"
                  }`}
              >
                <Square className="h-4 w-4" />
                {t("ไอคอน", "Icon")}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Generation Panel */}
            <div className="lg:col-span-2">
              <Card variant="glass">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {activeTab === "cover" ? (
                      <Image className="h-5 w-5 text-primary" />
                    ) : (
                      <Square className="h-5 w-5 text-green-500" />
                    )}
                    {activeTab === "cover"
                      ? t("สร้างภาพปก", "Create Cover")
                      : t("สร้างไอคอน", "Create Icon")
                    }
                  </CardTitle>
                  <CardDescription>
                    {activeTab === "cover"
                      ? t("เลือกประเภทปกและใส่คำอธิบายเพื่อสร้างภาพ", "Choose cover type and enter description to generate")
                      : t("สร้างไอคอนหรือโลโก้ขนาด 1:1 สำหรับโปรไฟล์หรือแบรนด์", "Create 1:1 icon or logo for profile or branding")
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Cover Type Selection - Only show for cover tab */}
                  {activeTab === "cover" && (
                    <div>
                      <label className="text-sm font-medium mb-3 block">{t("ประเภทปก", "Cover Type")}</label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {coverTypes.map((type) => {
                          const Icon = type.icon;
                          return (
                            <button
                              key={type.id}
                              onClick={() => setSelectedCoverType(type.id)}
                              disabled={(isGenerating && !allowNewGeneration) || !user}
                              className={`p-4 rounded-xl border text-left transition-all ${selectedCoverType === type.id
                                ? "border-primary bg-primary/10"
                                : "border-border hover:border-primary/50"
                                } ${(isGenerating && !allowNewGeneration) || !user ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type.id === "youtube" ? "bg-red-500/20 text-red-500" :
                                  type.id === "facebook" ? "bg-blue-500/20 text-blue-500" :
                                    "bg-purple-500/20 text-purple-500"
                                  }`}>
                                  <Icon className="h-5 w-5" />
                                </div>
                                <span className="font-medium">{type.name}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {t(type.descTh, type.descEn)}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Icon Info - Only show for icon tab */}
                  {activeTab === "icon" && (
                    <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                          <Square className="h-6 w-6 text-green-500" />
                        </div>
                        <div>
                          <p className="font-medium text-green-600">{t("ไอคอน / โปรไฟล์", "Icon / Profile")}</p>
                          <p className="text-sm text-muted-foreground">{t("ขนาด 1024x1024 px (อัตราส่วน 1:1)", "Size 1024x1024 px (1:1 ratio)")}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Model Selection */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t("โมเดล AI", "AI Model")}</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {photoModels.map((model) => (
                        <button
                          key={model.id}
                          onClick={() => setSelectedPhotoModel(model.id)}
                          disabled={(isGenerating && !allowNewGeneration) || !user}
                          className={`p-3 rounded-lg border text-left transition-all ${selectedPhotoModel === model.id
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/50"
                            } ${(isGenerating && !allowNewGeneration) || !user ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <p className="font-medium text-sm">{model.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {model.id === "nanobana-pro"
                              ? `${selectedResolution === "4K" ? 24 : 16} ${t("เครดิต", "credits")}`
                              : `${model.credits} ${t("เครดิต", "credits")}`
                            }
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Resolution Selection - Only for Nano Banana Pro */}
                  {selectedPhotoModel === "nanobana-pro" && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">{t("ความละเอียด", "Resolution")}</label>
                      <div className="grid grid-cols-3 gap-3">
                        {(["1K", "2K", "4K"] as const).map((res) => (
                          <button
                            key={res}
                            onClick={() => setSelectedResolution(res)}
                            disabled={(isGenerating && !allowNewGeneration) || !user}
                            className={`p-3 rounded-lg border text-center transition-all ${selectedResolution === res
                              ? "border-primary bg-primary/10"
                              : "border-border hover:border-primary/50"
                              } ${(isGenerating && !allowNewGeneration) || !user ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            <p className="font-bold text-lg">{res}</p>
                            <p className="text-xs text-muted-foreground">
                              {res === "4K" ? `24 ${t("เครดิต", "credits")}` : `16 ${t("เครดิต", "credits")}`}
                            </p>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t("เลือก 4K สำหรับภาพคมชัดพิเศษ (แนะนำสำหรับงานพิมพ์)", "Choose 4K for extra sharp images (recommended for print)")}
                      </p>
                    </div>
                  )}

                  {/* Prompt Templates - Show different templates based on tab */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t("เทมเพลตสำเร็จรูป", "Quick Templates")}</label>
                    <div className="flex flex-wrap gap-2">
                      {(activeTab === "cover" ? promptTemplates : iconPromptTemplates).map((template) => (
                        <Button
                          key={template.id}
                          variant="outline"
                          size="sm"
                          onClick={() => handleTemplateClick(template)}
                          disabled={(isGenerating && !allowNewGeneration) || !user}
                          className="gap-1"
                        >
                          <Wand2 className="h-3 w-3" />
                          {t(template.labelTh, template.labelEn)}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Prompt Input */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      {activeTab === "cover"
                        ? t("คำอธิบายภาพปก", "Cover Description")
                        : t("คำอธิบายไอคอน", "Icon Description")
                      }
                    </label>
                    <Textarea
                      placeholder={activeTab === "cover"
                        ? t("อธิบายภาพปกที่คุณต้องการ เช่น สไตล์ สี ธีม...", "Describe your cover image: style, colors, theme...")
                        : t("อธิบายไอคอนที่คุณต้องการ เช่น โลโก้ อวาตาร์ สไตล์...", "Describe your icon: logo, avatar, style...")
                      }
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      className="min-h-[120px] resize-none"
                      disabled={isGenerating && !allowNewGeneration}
                    />
                  </div>

                  {/* Credit Warning */}
                  {!hasEnoughCredits && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                      <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-destructive">
                          {t("เครดิตไม่เพียงพอ", "Insufficient Credits")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t(
                            `คุณต้องการ ${getRequiredCredits()} เครดิต`,
                            `You need ${getRequiredCredits()} credits`
                          )}
                        </p>
                        <Link to="/pricing">
                          <Button variant="outline" size="sm" className="mt-2 text-destructive border-destructive/30">
                            <Coins className="h-4 w-4 mr-2" />
                            {t("ซื้อเครดิต", "Buy Credits")}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Generate Button */}
                  <div className="relative">
                    {!user && (
                      <GuestOverlay
                        title={activeTab === "cover"
                          ? t("เข้าสู่ระบบเพื่อสร้างภาพปก", "Login to Create Covers")
                          : t("เข้าสู่ระบบเพื่อสร้างไอคอน", "Login to Create Icons")
                        }
                        description={activeTab === "cover"
                          ? t("สมัครสมาชิกเพื่อเริ่มสร้างภาพปกสวยๆ ด้วย AI", "Register to start creating beautiful covers with AI")
                          : t("สมัครสมาชิกเพื่อเริ่มสร้างไอคอนสวยๆ ด้วย AI", "Register to start creating beautiful icons with AI")
                        }
                        showIcon={false}
                      />
                    )}
                    <div className={!user ? "opacity-20 blur-[1px] pointer-events-none" : ""}>
                      <Button
                        variant="hero"
                        size="lg"
                        className="w-full gap-2"
                        onClick={handleGenerate}
                        disabled={(isGenerating && !allowNewGeneration) || !promptText.trim() || !hasEnoughCredits || !user}
                      >
                        {isGenerating && !allowNewGeneration ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            {t("กำลังสร้าง...", "Generating...")}
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-5 w-5" />
                            {activeTab === "cover"
                              ? t("สร้างภาพปก", "Generate Cover")
                              : t("สร้างไอคอน", "Generate Icon")
                            } ({getRequiredCredits()} {t("เครดิต", "credits")})
                          </>
                        )}
                      </Button>

                      {/* Continue without waiting button */}
                      {isGenerating && showContinueButton && !allowNewGeneration && (
                        <Button
                          variant="outline"
                          size="lg"
                          className="w-full gap-2 mt-2 border-primary/30 text-primary hover:bg-primary/10"
                          onClick={handleContinueWithoutWaiting}
                        >
                          <FastForward className="h-5 w-5" />
                          {t("สร้างต่อเลย ไม่รอ", "Continue, don't wait")}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Result Section - Shows immediately after generation */}
                  {(isGenerating || generatedResult) && (
                    <div className="mt-6 space-y-4">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-sm font-medium text-muted-foreground">
                          {t("ผลลัพธ์", "Result")}
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>

                      {isGenerating ? (
                        <div className="flex flex-col items-center justify-center py-12 rounded-xl bg-muted/30 border-2 border-dashed border-primary/30">
                          <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                          <p className="text-sm font-medium text-primary">
                            {t("กำลังสร้างภาพปก...", "Generating cover...")}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("โปรดรอสักครู่", "Please wait a moment")}
                          </p>
                        </div>
                      ) : generatedResult ? (
                        <div className="space-y-4">
                          {/* Generated Image */}
                          <div className="rounded-xl overflow-hidden border border-border shadow-lg">
                            <img
                              src={generatedResult}
                              alt="Generated Cover"
                              className="w-full h-auto object-contain bg-black/5"
                            />
                          </div>

                          {/* Success Message */}
                          {lastGeneration && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-green-600">
                                  {t("สร้างสำเร็จ!", "Generated successfully!")}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {lastGeneration.model} • -{lastGeneration.credits} {t("เครดิต", "credits")}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex flex-col gap-3">
                            <div className="flex gap-3">
                              <Button
                                variant="glow"
                                className="flex-1 gap-2"
                                onClick={() => handleDownload(generatedResult, `cover-${Date.now()}.png`)}
                              >
                                <Download className="h-4 w-4" />
                                {t("ดาวน์โหลด", "Download")}
                              </Button>
                              <Button
                                variant="outline"
                                className="gap-2"
                                onClick={() => {
                                  setGeneratedResult(null);
                                  setLastGeneration(null);
                                }}
                              >
                                <Sparkles className="h-4 w-4" />
                                {t("สร้างใหม่", "New")}
                              </Button>
                            </div>

                            {/* สร้างต่อ Button - Refresh page for continuous creation */}
                            <Button
                              variant="hero"
                              size="lg"
                              className="w-full gap-2"
                              onClick={() => window.location.reload()}
                            >
                              <RefreshCw className="h-5 w-5" />
                              {t("สร้างต่อ", "Continue Creating")}
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar - Tips & Info */}
            <div className="space-y-6">
              {/* Size Info - Changes based on active tab */}
              <Card variant="glass">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {activeTab === "cover"
                      ? t("ขนาดที่แนะนำ", "Recommended Sizes")
                      : t("ข้อมูลไอคอน", "Icon Info")
                    }
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {activeTab === "cover" ? (
                    <>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10">
                        <Youtube className="h-5 w-5 text-red-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">YouTube Banner</p>
                          <p className="text-xs text-muted-foreground">2560 x 1440 px</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10">
                        <Facebook className="h-5 w-5 text-blue-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">Facebook Cover</p>
                          <p className="text-xs text-muted-foreground">820 x 312 px</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-500/10">
                        <Monitor className="h-5 w-5 text-purple-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">YouTube Thumbnail</p>
                          <p className="text-xs text-muted-foreground">1280 x 720 px</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-green-500/10">
                        <Square className="h-5 w-5 text-green-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">{t("ไอคอน / โปรไฟล์", "Icon / Profile")}</p>
                          <p className="text-xs text-muted-foreground">1024 x 1024 px (1:1)</p>
                        </div>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-sm text-muted-foreground">
                          {t("เหมาะสำหรับ:", "Suitable for:")}
                        </p>
                        <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                          <li>• {t("รูปโปรไฟล์โซเชียลมีเดีย", "Social media profile pictures")}</li>
                          <li>• {t("โลโก้แบรนด์หรือช่อง", "Brand or channel logos")}</li>
                          <li>• {t("ไอคอน App หรือเว็บไซต์", "App or website icons")}</li>
                          <li>• {t("อวาตาร์สำหรับเกม", "Gaming avatars")}</li>
                        </ul>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Tips - Changes based on active tab */}
              <Card variant="glass">
                <CardHeader>
                  <CardTitle className="text-lg">{t("เคล็ดลับ", "Tips")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {activeTab === "cover" ? (
                      <>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{t("ใส่คำว่า 'banner' หรือ 'cover' ในคำอธิบาย", "Include 'banner' or 'cover' in description")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{t("ระบุสไตล์ที่ชัดเจน เช่น minimalist, colorful", "Specify clear style like minimalist, colorful")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{t("เพิ่ม '4K' หรือ 'high quality' เพื่อความคมชัด", "Add '4K' or 'high quality' for sharpness")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{t("เว้นพื้นที่สำหรับใส่ข้อความด้วยตัวเอง", "Leave space for adding your own text")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                          <span className="text-yellow-600 dark:text-yellow-400 font-medium">{t("💡 ถ้าต้องการใส่ข้อความภาษาไทย แนะนำเลือก Banana Pro เพราะเก่งภาษาไทย!", "💡 For Thai text, we recommend Banana Pro - it excels at Thai language!")}</span>
                        </li>
                      </>
                    ) : (
                      <>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{t("ใส่คำว่า 'icon' หรือ 'logo' ในคำอธิบาย", "Include 'icon' or 'logo' in description")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{t("ระบุ 'centered' เพื่อให้ภาพอยู่ตรงกลาง", "Specify 'centered' for center composition")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{t("ใช้ 'simple background' เพื่อพื้นหลังเรียบ", "Use 'simple background' for clean look")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span>{t("เพิ่ม 'vector style' สำหรับสไตล์โลโก้", "Add 'vector style' for logo-like style")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                          <span className="text-yellow-600 dark:text-yellow-400 font-medium">{t("💡 ถ้าต้องการใส่ข้อความภาษาไทย แนะนำเลือก Banana Pro เพราะเก่งภาษาไทย!", "💡 For Thai text, we recommend Banana Pro - it excels at Thai language!")}</span>
                        </li>
                      </>
                    )}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Recent History Section */}
          {user && (
            <div className="mt-12" id="recent-history-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-heading font-bold flex items-center gap-2">
                  <History className="h-5 w-5 text-primary" />
                  {t("ประวัติภาพปก", "Cover History")}
                </h2>
                <Button variant="outline" size="sm" onClick={() => navigate("/history")}>
                  {t("ดูทั้งหมด", "View All")}
                </Button>
              </div>

              {loadingCovers ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : recentCovers.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {recentCovers.map((log) => (
                    <Card key={log.id} variant="glass" className="overflow-hidden group">
                      {log.result_url && log.status === "completed" ? (
                        <div className="relative aspect-video bg-muted">
                          <img
                            src={log.result_url}
                            alt="Cover"
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-white hover:bg-white/20"
                              onClick={() => handleDownload(log.result_url!, `cover-${log.id}.png`)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-white hover:bg-white/20"
                              onClick={() => handleReusePrompt(log.prompt)}
                            >
                              <Repeat className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-white hover:bg-red-500/50"
                              onClick={() => handleDeleteHistory(log.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="aspect-video bg-muted flex items-center justify-center">
                          {log.status === "processing" || log.status === "pending" ? (
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                          ) : (
                            <AlertTriangle className="h-8 w-8 text-destructive" />
                          )}
                        </div>
                      )}
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-1">
                          {getStatusBadge(log.status)}
                          <span className="text-xs text-muted-foreground">
                            {log.credits_used} {t("เครดิต", "credits")}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">{log.prompt}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatDateHistory(log.created_at)}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card variant="glass" className="p-8 text-center">
                  <Image className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground">
                    {t("ยังไม่มีประวัติภาพปก", "No cover history yet")}
                  </p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    {t("สร้างภาพปกแรกของคุณเลย!", "Create your first cover!")}
                  </p>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CoverGenerator;
